/**
 * Document Hooks
 *
 * React Query hooks for document CRUD operations and file uploads.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTodayString } from "@src/packages/utils/date";
import { useApiClient } from "@src/packages/api/louise-api";
import { RequestBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";
import { throwApiError } from "@src/packages/api/api-error";
import {
  buildDownloadUrlRequest,
  buildDocumentMutationPath,
  getLegacyOllyDocumentIdFromUploadId,
} from "../document-request";
const IS_LOCAL_MOCK_MODE = process.env.EXPO_PUBLIC_DEV_LOCAL_MOCK === "true";

// =============================================================================
// Types
// =============================================================================

// Document from API response
export type Document = {
  id: string;
  patientId: string;
  name: string;
  documentDate: string | null;
  tags: string[];
  sharedWith: ("provider" | "patient" | "partner")[];
  createdBy: string;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  uploads: Upload[];
  origin?: "moon" | "legacy_olly";
  readOnly?: boolean;
  legacySourceId?: string | null;
};

export type Upload = {
  id: string;
  name: string | null;
  url: string | null;
  uploadStatus: "pending" | "uploaded" | null;
  contentType: string | null;
  size: string | null;
  notes: string | null;
  createdAt: string | null;
};

export {
  LEGACY_OLLY_UPLOAD_ID_PREFIX,
  getLegacyOllyDocumentIdFromUploadId,
} from "../document-request";

// Create document payload
type CreateDocumentPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}/documents"]["post"]
>;

// Update document payload
type UpdateDocumentPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}/documents/{documentId}"]["patch"]
>;

// Get upload URL payload
type GetUploadUrlPayload = RequestBody<
  paths["/{tenantId}/uploads/upload-url"]["post"]
>;

// =============================================================================
// LOCAL MOCK MODE (DEV ONLY)
// =============================================================================

const LOCAL_MOCK_DOCUMENTS_KEY = "louise_dev_mock_documents";

function getLocalMockDocuments(): Document[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = localStorage.getItem(LOCAL_MOCK_DOCUMENTS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn("[LocalMock] Failed to read documents from localStorage:", e);
  }
  return [];
}

function setLocalMockDocuments(documents: Document[]): Document[] {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(LOCAL_MOCK_DOCUMENTS_KEY, JSON.stringify(documents));
      console.log("[LocalMock] Documents updated:", documents);
    } catch (e) {
      console.warn("[LocalMock] Failed to write documents to localStorage:", e);
    }
  }
  return documents;
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Hook to get all documents for the current patient
 */
/**
 * @param opts.enabled Lets a caller that only needs the count behind a closed
 * surface avoid the round-trip until it opens. Defaults to fetching, which is
 * what every existing caller expects.
 */
export function useDocuments(opts?: { enabled?: boolean }) {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<Document[]>({
    queryKey: IS_LOCAL_MOCK_MODE
      ? ["documents", "local-mock"]
      : ["documents", tenantId, patientId],
    queryFn: async () => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log("[LocalMock] useDocuments: returning mock data");
        return getLocalMockDocuments();
      }

      // REAL API MODE
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/documents",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load documents",
          "documents",
          response?.status,
        );
      }
    },
    enabled:
      (opts?.enabled ?? true) &&
      (IS_LOCAL_MOCK_MODE ? true : !!patientId && !!tenantId),
    refetchOnWindowFocus: IS_LOCAL_MOCK_MODE ? true : undefined,
  });
}

/**
 * Hook to get a single document by ID
 */
export function useDocument(documentId: string) {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<Document>({
    queryKey: ["document", tenantId, patientId, documentId],
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}/documents/{documentId}",
        {
          params: {
            path: {
              tenantId,
              patientId,
              documentId,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load document",
          "document",
          response?.status,
        );
      }
    },
    enabled: !!patientId && !!tenantId && !!documentId,
  });
}

/**
 * Hook to get a presigned upload URL
 */
export function useGetUploadUrl() {
  const { client, tenantId } = useApiClient();

  return useMutation({
    mutationFn: async (payload: GetUploadUrlPayload) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log("[LocalMock] useGetUploadUrl: generating mock upload URL");
        await new Promise((resolve) => setTimeout(resolve, 100));
        return {
          uploadId: `mock-upload-${Date.now()}`,
          uploadUrl: "https://mock-upload-url.example.com",
        };
      }

      // REAL API MODE
      const { data, error, response } = await client.POST(
        "/{tenantId}/uploads/upload-url",
        {
          params: {
            path: {
              tenantId,
            },
          },
          body: payload,
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to create upload URL",
          "uploads",
          response?.status,
        );
      }
    },
  });
}

/**
 * Hook to upload a file to a presigned URL
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: async ({
      uploadUrl,
      file,
      contentType,
    }: {
      uploadUrl: string;
      file: Blob | ArrayBuffer;
      contentType: string;
    }) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log("[LocalMock] useUploadFile: simulating file upload");
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: true };
      }

      // REAL API MODE - Direct PUT to presigned URL
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": contentType,
        },
      });

      if (!response.ok) {
        throwApiError(
          undefined,
          `Upload failed: ${response.statusText}`,
          "uploads",
          response.status,
        );
      }

      return { success: true };
    },
  });
}

/**
 * Hook to mark an upload as complete
 * This must be called after uploading to the presigned URL
 */
export function useCompleteUpload() {
  const { client, tenantId } = useApiClient();

  return useMutation({
    mutationFn: async (uploadId: string) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log(
          "[LocalMock] useCompleteUpload: marking upload complete",
          uploadId,
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
        return { success: true };
      }

      // REAL API MODE - Note: This is a PUT request, not POST
      const { data, error, response } = await client.PUT(
        "/{tenantId}/uploads/{uploadId}/complete",
        {
          params: {
            path: {
              tenantId,
              uploadId,
            },
          },
        },
      );

      if (error) {
        throwApiError(
          error,
          "Failed to complete upload",
          "uploads",
          response?.status,
        );
      }

      return data?.data ?? { success: true };
    },
  });
}

/**
 * Hook to get a download URL for an upload
 */
// A bare uploadId keeps the default (inline) disposition for inline previews;
// pass { disposition: "attachment" } for an explicit download/open so the file
// saves under its real name instead of the bare S3 key. The disposition values
// are derived from the generated schema, not hand-written.
type DownloadDisposition = NonNullable<
  NonNullable<
    paths["/{tenantId}/uploads/{uploadId}/download-url"]["get"]["parameters"]["query"]
  >["disposition"]
>;
type GetDownloadUrlInput =
  string | { uploadId: string; disposition?: DownloadDisposition };

export function useGetDownloadUrl() {
  const { client, tenantId, patientId } = useApiClient();

  return useMutation({
    mutationFn: async (input: GetDownloadUrlInput) => {
      const uploadId = typeof input === "string" ? input : input.uploadId;
      const disposition =
        typeof input === "string" ? undefined : input.disposition;
      const query = disposition ? { disposition } : undefined;

      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log("[LocalMock] useGetDownloadUrl: returning mock URL");
        return "https://mock-download-url.example.com/file.jpg";
      }

      // REAL API MODE — the builder owns legacy-vs-upload routing and the
      // ownership identity carried in the path (#2245).
      const request = buildDownloadUrlRequest(
        { tenantId, patientId },
        uploadId,
        disposition,
      );
      if (request.kind === "legacy-olly") {
        const { data, error, response } = await client.GET(request.path, {
          params: request.params,
        });
        if (error) {
          throwApiError(
            error,
            "Failed to create a document download URL",
            "documents",
            response?.status,
          );
        }
        return data?.data ?? null;
      }

      const { data, error, response } = await client.GET(request.path, {
        params: request.params,
      });
      if (error) {
        throwApiError(
          error,
          "Failed to create a document download URL",
          "documents",
          response?.status,
        );
      }
      return data?.data ?? null;
    },
  });
}

/**
 * Hook to create a new document
 */
export function useCreateDocument() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = IS_LOCAL_MOCK_MODE
    ? ["documents", "local-mock"]
    : ["documents", tenantId, patientId];

  return useMutation({
    mutationFn: async (payload: CreateDocumentPayload) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log(
          "[LocalMock] useCreateDocument: creating document",
          payload,
        );
        await new Promise((resolve) => setTimeout(resolve, 100));

        const newDocument: Document = {
          id: `mock-doc-${Date.now()}`,
          patientId: "mock-patient-id",
          name: payload?.name || "Untitled Document",
          documentDate: payload?.documentDate || null,
          tags: payload?.tags || [],
          sharedWith: payload?.sharedWith || ["patient"],
          createdBy: "mock-user",
          createdAt: getTodayString(),
          updatedBy: null,
          updatedAt: null,
          uploads: (payload?.uploads || []).map((u) => ({
            id: u.id,
            name: null,
            url: null,
            uploadStatus: "uploaded" as const,
            contentType: null,
            size: null,
            notes: null,
            createdAt: getTodayString(),
          })),
        };

        const documents = getLocalMockDocuments();
        setLocalMockDocuments([...documents, newDocument]);

        return { data: newDocument };
      }

      // REAL API MODE
      if (!tenantId || !patientId) {
        throw new Error(
          "Cannot create document: missing tenantId or patientId",
        );
      }

      const { data, error, response } = await client.POST(
        "/{tenantId}/patients/{patientId}/documents",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
          body: payload,
        },
      );

      if (data) {
        return data;
      } else {
        console.error(
          "[useCreateDocument] backend rejected payload:",
          payload,
          "error:",
          error,
        );
        throwApiError(
          error,
          "Failed to create document",
          "documents",
          response?.status,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Hook to update an existing document
 */
export function useUpdateDocument() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = IS_LOCAL_MOCK_MODE
    ? ["documents", "local-mock"]
    : ["documents", tenantId, patientId];

  return useMutation({
    mutationFn: async ({
      documentId,
      data: payload,
    }: {
      documentId: string;
      data: UpdateDocumentPayload;
    }) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log(
          "[LocalMock] useUpdateDocument: updating document",
          documentId,
          payload,
        );
        await new Promise((resolve) => setTimeout(resolve, 100));

        const documents = getLocalMockDocuments();
        const updatedDocuments = documents.map((doc): Document => {
          if (doc.id !== documentId) return doc;

          // Extract uploads separately since it has different structure in payload vs Document
          const { uploads: payloadUploads, ...restPayload } = payload ?? {};

          // Handle uploads: filter out deleted ones, keep existing structure
          let updatedUploads = doc.uploads;
          if (payloadUploads) {
            const deleteIds = payloadUploads
              .filter((u) => u.op === "delete")
              .map((u) => u.id);
            updatedUploads = doc.uploads.filter(
              (u) => !deleteIds.includes(u.id),
            );
          }

          return {
            ...doc,
            ...restPayload,
            uploads: updatedUploads,
            updatedAt: getTodayString(),
          };
        });
        setLocalMockDocuments(updatedDocuments);

        return { data: updatedDocuments.find((d) => d.id === documentId) };
      }

      // REAL API MODE
      const request = buildDocumentMutationPath(
        { tenantId, patientId },
        "update document",
        documentId,
      );

      const { data, error, response } = await client.PATCH(request.path, {
        params: request.params,
        body: payload,
      });

      if (data) {
        return data;
      } else {
        throwApiError(
          error,
          "Failed to update document",
          "documents",
          response?.status,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Hook to delete a document
 */
export function useDeleteDocument() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  const queryKey = IS_LOCAL_MOCK_MODE
    ? ["documents", "local-mock"]
    : ["documents", tenantId, patientId];

  return useMutation({
    mutationFn: async (documentId: string) => {
      // LOCAL MOCK MODE
      if (IS_LOCAL_MOCK_MODE) {
        console.log(
          "[LocalMock] useDeleteDocument: deleting document",
          documentId,
        );
        await new Promise((resolve) => setTimeout(resolve, 100));

        const documents = getLocalMockDocuments();
        setLocalMockDocuments(documents.filter((d) => d.id !== documentId));

        return { success: true };
      }

      // REAL API MODE
      const request = buildDocumentMutationPath(
        { tenantId, patientId },
        "delete document",
        documentId,
      );

      const { error, response } = await client.DELETE(request.path, {
        params: request.params,
      });

      if (error) {
        throwApiError(
          error,
          "Failed to delete document",
          "documents",
          response?.status,
        );
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
